// color-exempt-file — retokenisation Phase 6 ui-themes : dark:bg-[#0a0a0a] preserve le rendu dark historique (voir memoire ui-themes)
import React, { Component, ErrorInfo, ReactNode } from "react";
import { logger } from "@core/lib/logger";
import i18n from "@core/i18n";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * ErrorBoundary pour attraper les erreurs React
 * Utilisé pour éviter que l'app crash complètement
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 🚨 LOGGING INTENSIF - Capture complète du contexte d'erreur
    const errorContext = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      errorInfo: {
        componentStack: errorInfo.componentStack,
        // Note: errorBoundaryName et errorBoundaryStack peuvent ne pas exister dans toutes les versions
        errorBoundaryName:
          ((errorInfo as Record<string, unknown>)
            .errorBoundaryName as string) || null,
        errorBoundaryStack:
          ((errorInfo as Record<string, unknown>)
            .errorBoundaryStack as string) || null,
      },
      performance: {
        memory: (
          performance as Performance & {
            memory?: {
              usedJSHeapSize: number;
              totalJSHeapSize: number;
              jsHeapSizeLimit: number;
            };
          }
        ).memory
          ? {
              usedJSHeapSize: (
                performance as Performance & {
                  memory: {
                    usedJSHeapSize: number;
                    totalJSHeapSize: number;
                    jsHeapSizeLimit: number;
                  };
                }
              ).memory.usedJSHeapSize,
              totalJSHeapSize: (
                performance as Performance & {
                  memory: {
                    usedJSHeapSize: number;
                    totalJSHeapSize: number;
                    jsHeapSizeLimit: number;
                  };
                }
              ).memory.totalJSHeapSize,
              jsHeapSizeLimit: (
                performance as Performance & {
                  memory: {
                    usedJSHeapSize: number;
                    totalJSHeapSize: number;
                    jsHeapSizeLimit: number;
                  };
                }
              ).memory.jsHeapSizeLimit,
            }
          : null,
        timing: {
          loadTime:
            performance.timing.loadEventEnd -
            performance.timing.navigationStart,
          domReady:
            performance.timing.domContentLoadedEventEnd -
            performance.timing.navigationStart,
        },
      },
      localStorage: {
        available: typeof Storage !== "undefined",
        quota:
          typeof navigator !== "undefined" && "storage" in navigator
            ? (
                navigator as Navigator & {
                  storage: { estimate(): Promise<StorageEstimate> };
                }
              ).storage.estimate
            : null,
      },
    };

    // Log détaillé pour debug
    console.error("🚨 ERROR BOUNDARY - CONTEXTE COMPLET:", errorContext);

    logger.error("React ErrorBoundary caught error", "general", errorContext);
  }

  public render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="min-h-screen-safe flex items-center justify-center bg-background dark:bg-[#0a0a0a]">
            <div className="text-center p-8">
              <h1 className="text-2xl font-bold text-foreground mb-4">
                {i18n.t("error_title")}
              </h1>
              <p className="text-muted-foreground mb-6">
                {i18n.t("unexpected_error")}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                data-testid="errorboundary-recharger-la-page"
              >
                {i18n.t("reload_page")}
              </button>
              {this.state.error && (
                <details className="mt-4 text-left">
                  <summary className="cursor-pointer text-sm text-muted-foreground">
                    {i18n.t("technical_details")}
                  </summary>
                  <pre className="mt-2 text-xs bg-muted p-4 rounded overflow-auto">
                    {this.state.error.message}
                    {"\n\n"}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
